import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import '../styles/dashboard.design.css';

function getUser() {
  try {
    const raw = localStorage.getItem('user');
    if (!raw) return { name: 'Administrador', role: 'Super Admin' };
    const parsed = JSON.parse(raw);
    // Try common fields
    const name = parsed.name || parsed.fullName || parsed.username || parsed.email || 'Usuario';
    const role = parsed.role || parsed.userRole || 'Usuario';
    const id = parsed.id ?? parsed.userId ?? null;
    return { id, name, role };
  } catch (e) {
    return { id: null, name: 'Administrador', role: 'Super Admin' };
  }
}


function initials(name) {
  if (!name) return 'US';
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 1) return parts[0].slice(0,2).toUpperCase();
  return (parts[0][0] + parts[parts.length-1][0]).toUpperCase();
}

export default function Dashboard() {
  const energyRef = useRef(null);
  const locationRef = useRef(null);
  const typesRef = useRef(null);
  const [devices, setDevices] = useState([]);
  const [stats, setStats] = useState({ total: 1, online: 0, alerts: 23, usage: 250 });
  const [alerts, setAlerts] = useState([]);
  const [energyData, setEnergyData] = useState([45, 52, 48, 65, 58, 72, 68]);
  const [energyLabels, setEnergyLabels] = useState(['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom']);
  const [locationData, setLocationData] = useState([65, 42, 38, 28, 25]);
  const [locationLabels, setLocationLabels] = useState(['Oficina', 'Almacén', 'Sala Reuniones', 'Pasillo', 'Área Trabajo']);
  const [typesData, setTypesData] = useState([35, 28, 22, 15]);
  const [typesLabels, setTypesLabels] = useState(['Sensores IoT', 'Climatización', 'Iluminación', 'Monitores']);
  const [calderaProgress, setCalderaProgress] = useState(0);
  const [calderaStatus, setCalderaStatus] = useState('OFFLINE');
  const [calderaError, setCalderaError] = useState(null);
  const [etapaNombre, setEtapaNombre] = useState('-');
  const [calderaSequenceName, setCalderaSequenceName] = useState('-');
  const [calderaSequenceTime, setCalderaSequenceTime] = useState(0);
  const [calderaFlameVoltage, setCalderaFlameVoltage] = useState(null);
  const [calderaTerminals, setCalderaTerminals] = useState({});
  const [calderaErrorCode, setCalderaErrorCode] = useState(null);
  const [calderaOnline, setCalderaOnline] = useState(false);
  const prevStageRef = useRef(null);
  const prevErrorRef = useRef(null);
  const prevProgressRef = useRef(0);

  const navigate = useNavigate();

  const user = getUser();

  // Drawing helper functions moved to component scope so multiple effects can use them
  const adjustColor = useCallback((color, percent) => {
    const num = parseInt(color.replace('#', ''), 16);
    const amt = Math.round(2.55 * percent);
    const R = ((num >> 16) & 0xFF) + amt;
    const G = ((num >> 8) & 0xFF) + amt;
    const B = (num & 0xFF) + amt;
    const r = Math.max(0, Math.min(255, R));
    const g = Math.max(0, Math.min(255, G));
    const b = Math.max(0, Math.min(255, B));
    return ('#' + ((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1));
  }, []);

  const drawLineChart = useCallback((ctx, data, labels, color, width, height) => {
    const padding = 40;
    const chartWidth = width - padding * 2;
    const chartHeight = height - padding * 2;
    const maxValue = Math.max(...data);
    const stepX = chartWidth / (data.length - 1 || 1);

    ctx.clearRect(0,0,width,height);
    ctx.strokeStyle = '#e2e8f0'; ctx.lineWidth = 1;
    for (let i=0;i<=4;i++){
      const y = padding + (chartHeight/4) * i;
      ctx.beginPath(); ctx.moveTo(padding,y); ctx.lineTo(width-padding,y); ctx.stroke();
    }

    ctx.strokeStyle = color; ctx.lineWidth = 3; ctx.beginPath();
    data.forEach((value,index)=>{
      const x = padding + stepX * index;
      const y = padding + chartHeight - (value/maxValue)*chartHeight;
      if (index===0) ctx.moveTo(x,y); else ctx.lineTo(x,y);
    });
    ctx.stroke();

    data.forEach((value,index)=>{
      const x = padding + stepX * index;
      const y = padding + chartHeight - (value/maxValue)*chartHeight;
      ctx.fillStyle = '#ffffff'; ctx.beginPath(); ctx.arc(x,y,6,0,Math.PI*2); ctx.fill();
      ctx.strokeStyle = color; ctx.lineWidth = 3; ctx.beginPath(); ctx.arc(x,y,6,0,Math.PI*2); ctx.stroke();
    });

    ctx.fillStyle = '#718096'; ctx.font = '12px Inter'; ctx.textAlign = 'center';
    labels.forEach((label,index)=>{ const x = padding + stepX * index; ctx.fillText(label, x, height-10); });
  }, []);

  const drawBarChart = useCallback((ctx, data, labels, color, width, height) => {
    const padding = 40;
    const chartWidth = width - padding * 2;
    const chartHeight = height - padding * 2;
    const maxValue = Math.max(...data);
    const barWidth = chartWidth / data.length * 0.7;
    const gap = chartWidth / data.length * 0.3;

    ctx.clearRect(0,0,width,height);
    data.forEach((value,index)=>{
      const x = padding + (barWidth + gap) * index + gap/2;
      const barHeight = (value/maxValue)*chartHeight;
      const y = padding + chartHeight - barHeight;
      const gradient = ctx.createLinearGradient(x,y,x,y+barHeight);
      gradient.addColorStop(0, color);
      gradient.addColorStop(1, color + 'CC');
      ctx.fillStyle = gradient; ctx.fillRect(x,y,barWidth,barHeight);
      ctx.fillStyle = '#2d3748'; ctx.font = 'bold 12px Inter'; ctx.textAlign='center'; ctx.fillText(value, x + barWidth/2, y - 5);
    });
    ctx.fillStyle = '#718096'; ctx.font='11px Inter'; ctx.textAlign='center';
    labels.forEach((label,index)=>{
      const x = padding + (barWidth + gap) * index + gap/2 + barWidth/2;
      const words = label.split(' ');
      words.forEach((word,wi)=> ctx.fillText(word, x, height - 15 + wi*12));
    });
  }, []);

  const drawPieChart = useCallback((ctx, data, labels, baseColor, width, height) => {
    const centerX = width/2; const centerY = height/2; const radius = Math.min(width,height)/2 - 40;
    const total = data.reduce((s,v)=>s+v,0) || 1;
    const colors = [baseColor, adjustColor(baseColor,20), adjustColor(baseColor,40), adjustColor(baseColor,60)];
    ctx.clearRect(0,0,width,height);
    let currentAngle = -Math.PI/2;
    data.forEach((value,index)=>{
      const sliceAngle = (value/total)*Math.PI*2;
      ctx.fillStyle = colors[index%colors.length]; ctx.beginPath(); ctx.moveTo(centerX,centerY);
      ctx.arc(centerX,centerY,radius,currentAngle,currentAngle+sliceAngle); ctx.closePath(); ctx.fill();
      const mid = currentAngle + sliceAngle/2; const tx = centerX + Math.cos(mid)*(radius*0.7); const ty = centerY + Math.sin(mid)*(radius*0.7);
      ctx.fillStyle = '#ffffff'; ctx.font='bold 14px Inter'; ctx.textAlign='center'; ctx.textBaseline='middle'; ctx.fillText(`${value}%`, tx, ty);
      currentAngle += sliceAngle;
    });
    const legendX = 10; let legendY = height - (labels.length * 25);
    labels.forEach((label,index)=>{
      ctx.fillStyle = colors[index%colors.length]; ctx.fillRect(legendX, legendY, 15, 15);
      ctx.fillStyle = '#2d3748'; ctx.font='11px Inter'; ctx.textAlign='left'; ctx.fillText(label, legendX + 20, legendY + 11);
      legendY += 25;
    });
  }, [adjustColor]);

  const getBarColor = useCallback(() => {
    if ((calderaStatus || '').toString().toUpperCase().includes('FAULT')) return '#f56565';
    // green while filling/operating
    if ((calderaProgress || 0) > 0) return '#38a169';
    // neutral
    return '#718096';
  }, [calderaStatus, calderaProgress]);

  // Map backend status values to progress % and human-friendly labels
  const mapStatusToProgress = useCallback((s) => {
    if (!s) return 0;
    const st = s.toString().toUpperCase();
    // Show 10% for STANDBY/ESPERA so the bar doesn't show 0 (which means INICIO)
    if (st.includes('STANDBY') || st.includes('ESPERA')) return 10;
    if (st.includes('PURGE') || st.includes('PURGA')) return 30;
    if (st.includes('PILOT') || st.includes('IGNIT') || st.includes('IGNIC')) return 60;
    if (st.includes('MAIN') || st.includes('PRINCIPAL')) return 80;
    if (st.includes('RUN') || st.includes('MARCHA')) return 100;
    return 0;
  }, []);

  const mapStatusToLabel = useCallback((s) => {
    if (!s) return 'Desconocida';
    const st = s.toString().toUpperCase();
    if (st.includes('STANDBY') || st.includes('ESPERA')) return 'Espera (Standby)';
    if (st.includes('PURGE') || st.includes('PURGA')) return 'Purgado de gases (Purge)';
    if (st.includes('PILOT') || st.includes('IGNIT') || st.includes('IGNIC')) return 'Encendido piloto (Pilot)';
    if (st.includes('MAIN') || st.includes('PRINCIPAL')) return 'Llama principal (Main)';
    if (st.includes('RUN') || st.includes('MARCHA')) return 'Operando (Run)';
    return s;
  }, []);

  const mapStatusToDescription = useCallback((s) => {
    if (!s) return 'Estado desconocido';
    const st = s.toString().toUpperCase();
    if (st.includes('STANDBY') || st.includes('ESPERA')) return 'Esperando demanda de calor';
    if (st.includes('PURGE') || st.includes('PURGA')) return 'Purgando gases — ventilador en marcha';
    if (st.includes('PILOT') || st.includes('IGNIT') || st.includes('IGNIC')) return 'Encendiendo piloto — verificando ignición';
    if (st.includes('MAIN') || st.includes('PRINCIPAL')) return 'Apertura válvula principal — llamas estabilizándose';
    if (st.includes('RUN') || st.includes('MARCHA')) return 'Operando normalmente';
    if (st.includes('FAULT')) return 'Fallo detectado — revisar alertas para detalles';
    return 'Estado: ' + s;
  }, []);

  useEffect(() => {
    const apiBaseRaw = process.env.REACT_APP_API_BASE_URL || '';
    const apiBase = apiBaseRaw.replace(/\/$/, '');

    // Shared helpers to compute progress from interpreted/register snapshot
    function computeProgressFromInterp(i) {
      const explicit = Number(i.progressPercent ?? i.progress ?? 0) || 0;
      if (explicit > 0) return Math.min(Math.max(explicit, 0), 100);

      const seqName = (i.sequenceName ?? i.sequence_name ?? i.status ?? i.stage ?? '').toString().toUpperCase();
      const t = Number(i.sequenceTimeSeconds ?? i.sequence_time_seconds ?? i.sequenceTime ?? 0);

      // If sequence explicitly contains HOLD (e.g. "PURGE HOLD", "FUEGO ALTO" hold), do not advance progress
      if (seqName.includes('HOLD')) {
        return Math.round(prevProgressRef.current || 0);
      }

      const stages = [
        { match: ['INICIO'], min: 0, max: 10, maxTime: 10, advance: true },
        { match: ['ESPERA','STANDBY'], advance: false, holdAt: () => (prevProgressRef.current || mapStatusToProgress('STANDBY') || 0) },
        // PURGE HOLD / HIGH FIRE SWITCH - these are hold states and should NOT advance progress
        { match: ['PURGA HOLD','PURGA HOLD HIGH','PURGA HOLD: FUEGO ALTO','PURGE HOLD','HIGH FIRE'], advance: false, holdAt: () => (prevProgressRef.current || 0) },
        // PURGA / PURGE proper: 0-30s -> progress 10-40%
        { match: ['PURGA','POSTPURGA','PURGE','PURGE DELAY'], min: 10, max: 40, maxTime: 30, advance: true },
        { match: ['PURGA HOLD LOW','FUEGO BAJO','PURGA HOLD: FUEGO BAJO'], advance: false, holdAt: () => (prevProgressRef.current || 40) },
        { match: ['IGNICION_PILOTO','PILOT','PILOTO','IGNICION_PILOTO'], min: 40, max: 70, maxTime: 10, advance: true },
        { match: ['IGNICION_PRINCIPAL','MAIN','PRINCIPAL'], min: 70, max: 90, maxTime: 10, advance: true },
        { match: ['MARCHA','RUN'], min: 90, max: 100, maxTime: 1, advance: true }
      ];

      let computed = null;
      for (const s of stages) {
        for (const m of s.match) {
          if (seqName.includes(m)) {
            if (s.advance === false) {
              try { computed = Number(s.holdAt()); } catch (e) { computed = Number(prevProgressRef.current || 0); }
            } else {
              const mt = Math.max(1, s.maxTime || 1);
              const ratio = Math.min(1, Math.max(0, t / mt));
              const p = s.min + ratio * (s.max - s.min);
              computed = p;
            }
            break;
          }
        }
        if (computed !== null) break;
      }

      if (computed === null) {
        computed = mapStatusToProgress(seqName || '');
      }

      // Ensure progress never decreases
      const nonDec = Math.max(prevProgressRef.current || 0, Number(computed || 0));
      return Math.round(nonDec);
    }

    function handleInterpreted(interp) {
      try {
        const statusRaw = interp.status ?? interp.state ?? interp.stage ?? interp.statusName ?? null;
        const statusStr = statusRaw ? String(statusRaw).toUpperCase() : null;
        const errorDesc = interp.errorDescription || interp.error || interp.error_description || null;
        const etapaLabel = mapStatusToLabel(statusStr || interp.status || interp.stage || interp.state);

        // Reset if entering INICIO
        const etapaUpperPre = (etapaLabel || '').toString().toUpperCase();
        if (etapaUpperPre.includes('INICIO')) prevProgressRef.current = 0;

        // If we recovered from an error and backend moved directly to STANDBY, reset progress to STANDBY (don't jump to INICIO)
        if ((prevErrorRef.current || (calderaStatus && String(calderaStatus).toUpperCase().includes('FAULT'))) && statusStr && (statusStr.includes('STANDBY') || statusStr.includes('ESPERA'))) {
          prevProgressRef.current = mapStatusToProgress('STANDBY') || 0;
          prevErrorRef.current = null;
        }

        let progress = computeProgressFromInterp(interp);

        // If there's an error reported, freeze progress at previous value
        if (errorDesc) {
          progress = prevProgressRef.current || progress || 0;
        } else {
          prevProgressRef.current = Math.max(prevProgressRef.current || 0, progress || 0);
        }

        const status = (errorDesc ? 'FAULT' : (interp.online ? 'RUNNING' : (statusStr || 'OFFLINE')));

        setCalderaProgress(progress);
        setCalderaStatus(status);
        setCalderaError(errorDesc);
        setEtapaNombre(etapaLabel);

        try { const seq = interp.sequenceName ?? interp.sequence_name ?? interp.sequence ?? '-'; setCalderaSequenceName(seq); } catch(e) {}
        try { setCalderaSequenceTime(Number(interp.sequenceTimeSeconds ?? interp.sequence_time_seconds ?? interp.sequenceTime ?? 0)); } catch(e) { setCalderaSequenceTime(0); }
        try { setCalderaFlameVoltage(interp.flameVoltage ?? interp.flame_voltage ?? null); } catch(e) { setCalderaFlameVoltage(null); }
        try { setCalderaTerminals(interp.terminals ?? {}); } catch(e) { setCalderaTerminals({}); }
        try { setCalderaErrorCode(interp.errorCode ?? interp.error_code ?? null); } catch(e) { setCalderaErrorCode(null); }
        try { setCalderaOnline(Boolean(interp.online)); } catch(e) { setCalderaOnline(Boolean(interp.online)); }

        // alerts on stage change
        const prev = prevStageRef.current;
        if (prev !== etapaLabel) {
          const title = `Etapa: ${etapaLabel}`;
          const desc = prev ? `Cambiada desde '${prev}'` : `Iniciada etapa '${etapaLabel}'`;
          setAlerts(a => [{ title, description: desc, time: new Date().toISOString() }, ...a].slice(0,8));
          setStats(s => ({ ...s, alerts: (s.alerts || 0) + 1 }));
          prevStageRef.current = etapaLabel;
        }

        // alerts on new error
        if (errorDesc && prevErrorRef.current !== errorDesc) {
          const title = `Falla detectada`;
          const desc = `${errorDesc}`;
          setAlerts(a => [{ title, description: desc, time: new Date().toISOString() }, ...a].slice(0,8));
          setStats(s => ({ ...s, alerts: (s.alerts || 0) + 1 }));
          prevErrorRef.current = errorDesc;
          setCalderaStatus('FAULT');
        }
      } catch (e) {
        console.warn('handleInterpreted error', e);
      }
    }

    // Try to load initial devices and stats (best-effort; endpoints should exist on your backend)
    (async function loadInitial() {
      try {
        // determine current user id
        let userId = user && user.id ? user.id : null;
        if (!userId) {
          const rawUser = localStorage.getItem('user');
          try { userId = rawUser ? JSON.parse(rawUser).id : null; } catch(e) { userId = null; }
        }

        if (userId) {
          // try mounted api path first, fall back to non-api
          const tryUrls = [`${apiBase}/api/user/${userId}/devices`, `${apiBase}/user/${userId}/devices`];
          for (const u of tryUrls) {
            try {
              const r = await fetch(u);
              console.debug('[dashboard] GET', u, 'status', r.status);
              if (!r.ok) continue;
              const j = await r.json();
              console.debug('[dashboard] GET payload', j);
              const rows = Array.isArray(j.devices) ? j.devices : (Array.isArray(j) ? j : null);
              // normalize even if rows is empty array
              if (rows !== null) {
                // normalize server fields to frontend shape
                const norm = rows.map(row => {
                  const token = row.token ?? row.device_token ?? null;
                  const numericId = (row.device_id ?? row.id) != null ? Number(row.device_id ?? row.id) : null;
                  return ({
                    // keep numeric id if present; also expose token
                    id: numericId,
                    device_id: numericId,
                    token,
                    name: row.name ?? token ?? `Dispositivo ${numericId ?? token ?? '—'}`,
                    type: row.device_type ?? row.type,
                    location: row.location ?? '',
                    // device_is_online comes from devices table, status_is_online from device_status
                    is_online: (row.device_is_online !== undefined) ? Boolean(row.device_is_online) : Boolean(row.is_online ?? 0),
                    status_is_online: (row.status_is_online !== undefined) ? Boolean(row.status_is_online) : Boolean(row.is_online ?? 0),
                    led_state: row.led_state ?? null,
                    current_consumption: row.current_consumption ?? row.power_consumption ?? row.consumption ?? null,
                    last_activity: row.device_last_activity ?? row.last_activity ?? null,
                    status_last_activity: row.status_last_activity ?? row.last_activity ?? null,
                    role: row.role ?? null,
                    registers: row.registers ?? {}
                  });
                });
                setDevices(norm);
                // Initialize caldera progress from any available interpreted/register snapshot
                try {
                  const candidate = norm.find(r => (r.registers && Object.keys(r.registers).length > 0) || r.interpreted) || null;
                  if (candidate) {
                    if (candidate.interpreted) {
                      handleInterpreted(candidate.interpreted);
                    } else if (candidate.registers) {
                      const registers = candidate.registers || {};
                      const regStage = registers['2'] ?? registers[2] ?? registers['3'] ?? registers[3] ?? null;
                      const startupCounter = Number(registers['5'] ?? registers['startupCounter'] ?? registers['start'] ?? 0);
                      const errorCode = Number(registers['0'] ?? registers['error'] ?? 0);
                      let fallbackStatus = 'OFFLINE';
                      if (errorCode !== 0) fallbackStatus = 'FAULT';
                      else if (regStage) {
                        const rs = String(regStage).toUpperCase();
                        if (rs.includes('PURGE') || rs.includes('PURGA')) fallbackStatus = 'PURGA';
                        else if (rs.includes('PILOT') || rs.includes('PILOTO') || rs.includes('IGNIT')) fallbackStatus = 'IGNICION_PILOTO';
                        else if (rs.includes('MAIN') || rs.includes('PRINCIPAL')) fallbackStatus = 'IGNICION_PRINCIPAL';
                        else if (rs.includes('RUN') || rs.includes('MARCHA')) fallbackStatus = 'MARCHA';
                        else if (rs.includes('STANDBY') || rs.includes('ESPERA')) fallbackStatus = 'ESPERA';
                      } else if (startupCounter > 0 && startupCounter < 30) fallbackStatus = 'STARTING';
                      else if (startupCounter >= 30) fallbackStatus = 'RUNNING';

                      const fakeInterp = { status: fallbackStatus, sequenceName: regStage ? String(regStage) : null, sequenceTimeSeconds: startupCounter, errorDescription: errorCode !== 0 ? `Falla código ${errorCode}` : null, online: true };
                      if (String(fallbackStatus).toUpperCase().includes('HOLD')) {
                        fakeInterp.progress = prevProgressRef.current || 0;
                      }
                      handleInterpreted(fakeInterp);
                    }
                  }
                } catch (e) { console.debug('init interpret error', e); }
                break;
              }
            } catch (e) { continue; }
          }
        }
      } catch (e) {
        // ignore, we'll get realtime updates via WS
      }

      try {
        const statsRes = await fetch(`${apiBase}/api/dashboard/stats`);
        if (statsRes.ok) {
          const js = await statsRes.json();
          setStats(prev => ({ ...prev, ...js }));
        }
      } catch (e) {}
    })();

    function adjustCanvasSize(canvas) {
      if (!canvas) return;
      const rect = canvas.getBoundingClientRect();
      canvas.width = rect.width;
      canvas.height = rect.height;
    }

    // keep this effect focused on initial data load only
    // drawing and resize handling are performed in a separate effect
    // inject small keyframes for purge pulse if not present
    try {
      if (typeof document !== 'undefined' && !document.getElementById('dashboard-pulse-styles')) {
        const style = document.createElement('style');
        style.id = 'dashboard-pulse-styles';
        style.innerHTML = `@keyframes kpulse { 0% { filter: drop-shadow(0 0 0 rgba(236,103,59,0.0)); } 50% { filter: drop-shadow(0 0 14px rgba(236,103,59,0.18)); } 100% { filter: drop-shadow(0 0 0 rgba(236,103,59,0.0)); } } .purge-pulse { animation: kpulse 1.4s infinite; }`;
        document.head.appendChild(style);
      }
    } catch (e) {}

    return () => {};
  }, []);

  // Redraw when data changes
  useEffect(() => {
    const config = window.elementSdk ? window.elementSdk.config : { primary_color: '#ec673b' };
    const primaryColor = config.primary_color || '#ec673b';

    function doDraw() {
      if (energyRef.current) {
        const ctx = energyRef.current.getContext('2d');
        drawLineChart(ctx, energyData, energyLabels, primaryColor, energyRef.current.width, energyRef.current.height);
      }
      if (locationRef.current) {
        const ctx2 = locationRef.current.getContext('2d');
        drawBarChart(ctx2, locationData, locationLabels, primaryColor, locationRef.current.width, locationRef.current.height);
      }
      if (typesRef.current) {
        const ctx3 = typesRef.current.getContext('2d');
        drawPieChart(ctx3, typesData, typesLabels, primaryColor, typesRef.current.width, typesRef.current.height);
      }
    }

    // slight delay to ensure canvases are sized after layout changes
    const t = setTimeout(doDraw, 50);
    const onResize = () => { doDraw(); };
    window.addEventListener('resize', onResize);
    return () => { clearTimeout(t); window.removeEventListener('resize', onResize); };
  }, [energyData, energyLabels, locationData, locationLabels, typesData, typesLabels, drawLineChart, drawBarChart, drawPieChart]);

  const apiBaseRawTop = process.env.REACT_APP_API_BASE_URL || '';
  const apiBaseTop = apiBaseRawTop.replace(/\/$/, '');

  async function toggleLed(deviceId, currentState, role) {
    if (role && role !== 'admin') {
      setAlerts(a => [{ title: 'Permisos', description: 'No tiene permiso para cambiar este dispositivo', time: new Date().toISOString() }, ...a].slice(0,8));
      return;
    }
    const target = !currentState;
    // optimistic update
    setDevices(prev => prev.map(d => (d.id === deviceId || d.device_id === deviceId) ? ({ ...d, led_state: target }) : d));
    try {
      const urls = [`${apiBaseTop}/api/devices/${deviceId}/toggle`, `${apiBaseTop}/devices/${deviceId}/toggle`];
      let ok = false;
      for (const u of urls) {
        try {
          const r = await fetch(u, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ state: target }) });
          if (r.ok) { ok = true; break; }
        } catch (e) { continue; }
      }
      if (!ok) throw new Error('No se pudo contactar API');
    } catch (err) {
      setAlerts(a => [{ title: 'Error', description: 'No se pudo cambiar el estado del LED', time: new Date().toISOString() }, ...a].slice(0,8));
      // revert
      setDevices(prev => prev.map(d => (d.id === deviceId || d.device_id === deviceId) ? ({ ...d, led_state: currentState }) : d));
    }
  }

  // WebSocket connection to receive realtime updates
  useEffect(() => {
    let ws;
    let reconnectTimer = null;

    const rawWs = (process.env.REACT_APP_WS_URL || '').trim();
    let wsUrl = '';
    if (rawWs) {
      if (rawWs.startsWith('ws://') || rawWs.startsWith('wss://')) {
        wsUrl = rawWs.replace(/\/$/, '');
      } else if (rawWs.startsWith('http://')) {
        wsUrl = 'ws://' + rawWs.replace(/^http:\/\//, '').replace(/\/$/, '') + '/ws';
      } else if (rawWs.startsWith('https://')) {
        wsUrl = 'wss://' + rawWs.replace(/^https:\/\//, '').replace(/\/$/, '') + '/ws';
      } else {
        wsUrl = rawWs.replace(/\/$/, '');
      }
    } else {
      wsUrl = `${window.location.protocol === 'https:' ? 'wss' : 'ws'}://${window.location.host}/ws`;
    }

    function connect() {
      try {
        ws = new WebSocket(wsUrl);
      } catch (e) {
        console.error('WS connect error', e);
        scheduleReconnect();
        return;
      }

      ws.onopen = () => {
        //console.info('WebSocket connected', wsUrl);
        // ask server for initial snapshot and include current user id when available
        const rawUser = localStorage.getItem('user');
        let userId = null;
        try { userId = rawUser ? JSON.parse(rawUser).id : null; } catch(e) { userId = null; }
        const snap = userId ? { type: 'snapshot', user_id: userId } : { type: 'snapshot' };
        if (ws.readyState === WebSocket.OPEN) ws.send(JSON.stringify(snap));
      };
      // helper to normalize interpreted payloads and update UI + alerts
      function handleInterpreted(interp) {
        try {
          const statusRaw = interp.status ?? interp.state ?? interp.stage ?? interp.statusName ?? null;
          const statusStr = statusRaw ? String(statusRaw).toUpperCase() : null;
          const errorDesc = interp.errorDescription || interp.error || interp.error_description || null;
          const etapaLabel = mapStatusToLabel(statusStr || interp.status || interp.stage || interp.state);

          // Determine if this is INICIO so we can reset progress before computing
          const etapaUpperPre = (etapaLabel || '').toString().toUpperCase();
          if (etapaUpperPre.includes('INICIO')) {
            prevProgressRef.current = 0;
          }

          // If we recovered from an error and backend moved directly to STANDBY, reset progress to STANDBY
          if ((prevErrorRef.current || (calderaStatus && String(calderaStatus).toUpperCase().includes('FAULT'))) && statusStr && (statusStr.includes('STANDBY') || statusStr.includes('ESPERA'))) {
            prevProgressRef.current = mapStatusToProgress('STANDBY') || 0;
            prevErrorRef.current = null;
          }

          // Compute progress based on Honeywell sequence and elapsed time
          function computeProgressFromInterp(i) {
            // prefer explicit progress if provided
            const explicit = Number(i.progressPercent ?? i.progress ?? 0) || 0;
            if (explicit > 0) return Math.min(Math.max(explicit, 0), 100);

            const seqName = (i.sequenceName ?? i.sequence_name ?? i.status ?? i.stage ?? '').toString().toUpperCase();
            const t = Number(i.sequenceTimeSeconds ?? i.sequence_time_seconds ?? i.sequenceTime ?? 0);

            // Do not advance when sequence contains HOLD
            if (seqName.includes('HOLD')) {
              return Math.round(prevProgressRef.current || 0);
            }

            const stages = [
              // INICIO: 0-10s -> progress 0-10%
              { match: ['INICIO'], min: 0, max: 10, maxTime: 10, advance: true },
              // ESPERA/STANDBY: counts but does NOT advance progress (hold)
                  { match: ['ESPERA','STANDBY'], advance: false, holdAt: () => (prevProgressRef.current || mapStatusToProgress('STANDBY') || 0) },
              // PURGE HOLD / HIGH FIRE SWITCH - hold state, DO NOT advance
              { match: ['PURGA HOLD','PURGA HOLD HIGH','PURGA HOLD: FUEGO ALTO','PURGE HOLD','HIGH FIRE'], advance: false, holdAt: () => (prevProgressRef.current || 0) },
              // PURGA / PURGE proper: include purge time (up to 30s) into progress 10-40%
              { match: ['PURGA','POSTPURGA','PURGE','PURGE DELAY'], min: 10, max: 40, maxTime: 30, advance: true },
              // PURGA HOLD LOW: hold until switch triggers
              { match: ['PURGA HOLD LOW','FUEGO BAJO','PURGA HOLD: FUEGO BAJA'], advance: false, holdAt: () => (prevProgressRef.current || 40) },
              // PILOTO IGN: include up to 10s -> progress 40-70%
              { match: ['IGNICION_PILOTO','PILOT','PILOTO','IGNICION_PILOTO'], min: 40, max: 70, maxTime: 10, advance: true },
              // IGNICION PRINCIPAL / MAIN
              { match: ['IGNICION_PRINCIPAL','MAIN','PRINCIPAL'], min: 70, max: 90, maxTime: 10, advance: true },
              // MARCHA / RUN -> final
              { match: ['MARCHA','RUN'], min: 90, max: 100, maxTime: 1, advance: true }
            ];

            let computed = null;
            for (const s of stages) {
              for (const m of s.match) {
                if (seqName.includes(m)) {
                  if (s.advance === false) {
                    try { computed = Number(s.holdAt()); } catch (e) { computed = Number(prevProgressRef.current || 0); }
                  } else {
                    const mt = Math.max(1, s.maxTime || 1);
                    const ratio = Math.min(1, Math.max(0, t / mt));
                    const p = s.min + ratio * (s.max - s.min);
                    computed = p;
                  }
                  break;
                }
              }
              if (computed !== null) break;
            }

            if (computed === null) {
              computed = mapStatusToProgress(statusStr || seqName || '');
            }

            // Ensure progress never decreases (avoid visual regression)
            const nonDec = Math.max(prevProgressRef.current || 0, Number(computed || 0));
            return Math.round(nonDec);

            // fallback to generic mapping
            return mapStatusToProgress(statusStr || seqName || '');
          }

          let progress = computeProgressFromInterp(interp);

          // If there's an error reported, freeze progress at previous value
          if (errorDesc) {
            progress = prevProgressRef.current || progress || 0;
          } else {
            // ensure we store the latest non-fault progress (monotonic)
            prevProgressRef.current = Math.max(prevProgressRef.current || 0, progress || 0);
          }

          const status = (errorDesc ? 'FAULT' : (interp.online ? 'RUNNING' : (statusStr || 'OFFLINE')));

          setCalderaProgress(progress);
          setCalderaStatus(status);
          setCalderaError(errorDesc);
          setEtapaNombre(etapaLabel);

          // additional interpreted fields from backend
          try {
            const seq = interp.sequenceName ?? interp.sequence_name ?? interp.sequence ?? '-';
            setCalderaSequenceName(seq);
          } catch(e) {}
          try { setCalderaSequenceTime(Number(interp.sequenceTimeSeconds ?? interp.sequence_time_seconds ?? interp.sequenceTime ?? 0)); } catch(e) { setCalderaSequenceTime(0); }
          try { setCalderaFlameVoltage(interp.flameVoltage ?? interp.flame_voltage ?? null); } catch(e) { setCalderaFlameVoltage(null); }
          try { setCalderaTerminals(interp.terminals ?? {}); } catch(e) { setCalderaTerminals({}); }
          try { setCalderaErrorCode(interp.errorCode ?? interp.error_code ?? null); } catch(e) { setCalderaErrorCode(null); }
          try { setCalderaOnline(Boolean(interp.online)); } catch(e) { setCalderaOnline(Boolean(interp.online)); }

          // alerts on stage change
          const prev = prevStageRef.current;
          if (prev !== etapaLabel) {
            const title = `Etapa: ${etapaLabel}`;
            const desc = prev ? `Cambiada desde '${prev}'` : `Iniciada etapa '${etapaLabel}'`;
            setAlerts(a => [{ title, description: desc, time: new Date().toISOString() }, ...a].slice(0,8));
            setStats(s => ({ ...s, alerts: (s.alerts || 0) + 1 }));
            prevStageRef.current = etapaLabel;
          }

          // alerts on new error
          if (errorDesc && prevErrorRef.current !== errorDesc) {
            const title = `Falla detectada`;
            const desc = `${errorDesc}`;
            setAlerts(a => [{ title, description: desc, time: new Date().toISOString() }, ...a].slice(0,8));
            setStats(s => ({ ...s, alerts: (s.alerts || 0) + 1 }));
            prevErrorRef.current = errorDesc;
            setCalderaStatus('FAULT');
          }
        } catch (e) {
          console.warn('handleInterpreted error', e);
        }
      }

      ws.onmessage = (ev) => {
        try {
            const payload = JSON.parse(ev.data);
            // debug incoming ws messages
            console.debug('[dashboard] WS message', payload.type || payload.event || 'no-type', payload);
            // Support interpreted_state messages from backend
            if (payload.type === 'interpreted_state') {
              const data = payload.data || payload;
              handleInterpreted(data);
            }

            // Handle simple event messages (deviceEventService) - offline/online/falla
            if (payload.type === 'offline' || payload.event_type === 'offline' || payload.event === 'offline') {
              // update global caldera UI
              setCalderaStatus('OFFLINE');
              setCalderaError(null);
              setCalderaProgress(0);
              // if device id provided, update devices list state
              const did = payload.deviceId ?? payload.device_id ?? (payload.data && (payload.data.deviceId ?? payload.data.device_id)) ?? null;
              if (did) {
                const idStr = String(did);
                setDevices(prev => prev.map(d => (([d.id, d.device_id, d.token].some(x => x != null && String(x) === idStr)) ? ({ ...d, status_is_online: 0 }) : d)));
              }
            }
            if (payload.type === 'online' || payload.event_type === 'online' || payload.event === 'online') {
              setCalderaStatus('STARTING');
              setCalderaError(null);
              const did = payload.deviceId ?? payload.device_id ?? (payload.data && (payload.data.deviceId ?? payload.data.device_id)) ?? null;
              if (did) {
                const idStr = String(did);
                setDevices(prev => prev.map(d => (([d.id, d.device_id, d.token].some(x => x != null && String(x) === idStr)) ? ({ ...d, status_is_online: 1 }) : d)));
              }
            }
            if (payload.type === 'falla' || payload.event_type === 'falla' || payload.event === 'falla') {
              const code = payload.value ?? (payload.data && payload.data.errorCode) ?? null;
              const desc = payload.description || (payload.data && payload.data.errorDescription) || `Falla código ${code}`;
              setCalderaStatus('FAULT');
              setCalderaError(desc);
            }
          // flexible handlers depending on server message shape
          if (payload.type === 'stats' && payload.data) {
            setStats(prev => ({ ...prev, ...payload.data }));
          }
          if (payload.type === 'devices' && Array.isArray(payload.data)) {
            setDevices(payload.data);
          }
          if (payload.type === 'energy' && payload.data) {
            if (Array.isArray(payload.data.values)) setEnergyData(payload.data.values);
            if (Array.isArray(payload.data.labels)) setEnergyLabels(payload.data.labels);
          }
          if (payload.type === 'locations' && payload.data) {
            if (Array.isArray(payload.data.values)) setLocationData(payload.data.values);
            if (Array.isArray(payload.data.labels)) setLocationLabels(payload.data.labels);
          }
          if (payload.type === 'types' && payload.data) {
            if (Array.isArray(payload.data.values)) setTypesData(payload.data.values);
            if (Array.isArray(payload.data.labels)) setTypesLabels(payload.data.labels);
          }
          if (payload.type === 'alert') {
            setStats(prev => ({ ...prev, alerts: (prev.alerts || 0) + 1 }));
            setAlerts(a => [{ title: payload.title || 'Alerta', description: payload.description || '', time: new Date().toISOString() }, ...a].slice(0,8));
          }

          // Handle device update broadcast from wsServer (your wsServer broadcasts type 'DEVICE_UPDATE')
          if (payload.type === 'DEVICE_UPDATE' || payload.type === 'device_update') {
            const deviceId = payload.deviceId ?? payload.device_id ?? null;
            const registers = payload.registers || {};
            // If server included 'interpreted' data, prefer that
            const interpreted = payload.interpreted || payload.interpretedState || payload.interpreted_state || null;
            if (interpreted) {
              handleInterpreted(interpreted);
            } else {
              // Fallback: try to interpret register 3 (index 2) or startup counter
              const regStage = registers['2'] ?? registers[2] ?? registers['3'] ?? registers[3] ?? null;
              const startupCounter = Number(registers['5'] ?? registers['startupCounter'] ?? registers['start'] ?? 0);
              const errorCode = Number(registers['0'] ?? registers['error'] ?? 0);
              let fallbackStatus = 'OFFLINE';
              if (errorCode !== 0) fallbackStatus = 'FAULT';
              else if (regStage) {
                // if regStage is textual, use it; if numeric, map numeric heuristics
                const rs = String(regStage).toUpperCase();
                if (rs.includes('HOLD')) fallbackStatus = 'PURGE_HOLD';
                else if (rs.includes('PURGE') || rs.includes('PURGA')) fallbackStatus = 'PURGE';
                else if (rs.includes('PILOT') || rs.includes('PILOTO') || rs.includes('IGNIT')) fallbackStatus = 'PILOT';
                else if (rs.includes('MAIN') || rs.includes('PRINCIPAL')) fallbackStatus = 'MAIN';
                else if (rs.includes('RUN') || rs.includes('MARCHA')) fallbackStatus = 'RUN';
                else if (rs.includes('STANDBY') || rs.includes('ESPERA')) fallbackStatus = 'STANDBY';
              } else if (startupCounter > 0 && startupCounter < 30) fallbackStatus = 'STARTING';
              else if (startupCounter >= 30) fallbackStatus = 'RUNNING';

              const fakeInterp = { status: fallbackStatus, errorDescription: errorCode !== 0 ? `Falla código ${errorCode}` : null };
              if (String(fallbackStatus).toUpperCase().includes('HOLD')) {
                fakeInterp.progress = prevProgressRef.current || 0;
              } else {
                fakeInterp.progress = mapStatusToProgress(fallbackStatus);
              }
              handleInterpreted(fakeInterp);
            }

            // merge device status info returned by server into devices list
            const statusRow = payload.status || payload.device_status || (payload.interpreted && payload.interpreted) || null;
            setDevices(prev => {
              const idStr = deviceId != null ? String(deviceId) : null;
              const idx = prev.findIndex(d => {
                if (!idStr) return false;
                return [d.id, d.device_id, d.token].some(x => x != null && String(x) === idStr);
              });
              const now = new Date().toISOString();
              if (idx === -1) {
                // create placeholder entry
                const isNumeric = idStr && /^[0-9]+$/.test(idStr);
                const newDevice = {
                  id: isNumeric ? Number(idStr) : null,
                  device_id: isNumeric ? Number(idStr) : idStr,
                  token: payload.token ?? (isNumeric ? null : idStr),
                  name: payload.name || registers.name || payload.token || `Dispositivo ${idStr}`,
                  type: registers.type || payload.device_type || 'Desconocido',
                  location: registers.location || payload.location || '',
                  is_online: (payload.is_online !== undefined) ? Boolean(payload.is_online) : Boolean(payload.isOnline ?? true),
                  status_is_online: (statusRow && (statusRow.is_online !== undefined)) ? Boolean(statusRow.is_online) : ((payload.is_online !== undefined) ? Boolean(payload.is_online) : true),
                  led_state: statusRow ? (statusRow.led_state ?? null) : null,
                  current_consumption: statusRow ? (statusRow.current_consumption ?? null) : (registers.energy || registers.consumption || null),
                  last_activity: now,
                  status_last_activity: statusRow ? (statusRow.last_activity ?? now) : now,
                  role: payload.role ?? null,
                  registers
                };
                return [newDevice, ...prev];
              }

              const existing = { ...prev[idx] };
              const previousRegisters = existing.registers || {};
              existing.registers = registers;
              existing.is_online = (payload.is_online !== undefined) ? Boolean(payload.is_online) : existing.is_online;
              existing.status_is_online = (statusRow && statusRow.is_online !== undefined) ? Boolean(statusRow.is_online) : existing.status_is_online;
              existing.last_activity = now;
              existing.status_last_activity = statusRow ? (statusRow.last_activity ?? existing.status_last_activity) : existing.status_last_activity;
              existing.led_state = statusRow ? (statusRow.led_state ?? existing.led_state) : existing.led_state;
              existing.current_consumption = statusRow ? (statusRow.current_consumption ?? existing.current_consumption) : (registers.energy || registers.consumption || existing.current_consumption);
              existing.role = payload.role ?? existing.role;

              // detect bit changes on register 12 and push alerts
              if (previousRegisters && previousRegisters['12'] !== undefined && registers['12'] !== undefined) {
                const oldVal = Number(previousRegisters['12']);
                const newVal = Number(registers['12']);
                for (let bit=0; bit<16; bit++) {
                  const oldBit = (oldVal >> bit) & 1;
                  const newBit = (newVal >> bit) & 1;
                  if (oldBit !== newBit) {
                    const title = `Cambio en device ${deviceId} - bit ${bit}`;
                    const description = `Bit ${bit} cambió a ${newBit}`;
                    setAlerts(a => [{ title, description, time: new Date().toISOString() }, ...a].slice(0,8));
                    setStats(prevS => ({ ...prevS, alerts: (prevS.alerts || 0) + 1 }));
                  }
                }
              }

              const copy = [...prev];
              copy[idx] = existing;
              return copy;
            });
          }
          // fallback: if server sends a root object with known props
          if (!payload.type) {
            if (payload.stats) setStats(prev => ({ ...prev, ...payload.stats }));
            if (payload.devices) setDevices(payload.devices);
            if (payload.energy) { if (payload.energy.values) setEnergyData(payload.energy.values); if (payload.energy.labels) setEnergyLabels(payload.energy.labels); }
          }
        } catch (err) {
          console.warn('WS message parse error', err);
        }
      };

      ws.onclose = (ev) => {
        //console.warn('WebSocket closed', ev.code, ev.reason);
        scheduleReconnect();
      };

      ws.onerror = (err) => {
        console.error('WebSocket error', err);
        ws.close();
      };
    }

    function scheduleReconnect() {
      if (reconnectTimer) return;
      reconnectTimer = setTimeout(() => {
        reconnectTimer = null;
        connect();
      }, 3000);
    }

    connect();

    return () => {
      if (reconnectTimer) clearTimeout(reconnectTimer);
      if (ws && ws.readyState === WebSocket.OPEN) ws.close();
    };
  }, []);

  return (
    <div className="dashboard-wrapper">
      <div className="dashboard-container">
        <header className="dashboard-header">
          <div className="header-left">
            <img src="/logo512.png" alt="Ker Logo" className="logo-icon" />
            <div className="header-text">
              <div className="company-name">Bienvenido/a </div>
              <h1 className="dashboard-title">Panel de Control</h1>
              <p className="dashboard-subtitle">Monitoreo en tiempo real</p>
            </div>
          </div>

          <div className="header-right" >
            <div className="user-info" onClick={() => navigate('/profile')} role="button" style={{cursor: 'pointer'}}>
              <div className="user-avatar">{initials(user.name)}</div>
              <div className="user-details">
                <div className="user-name">{user.name}</div>
                <div className="user-role">{user.role}</div>
              </div>
            </div>
          </div>
        </header>
        <div className="caldera-content">
          <div className="caldera-header">
            <h2 className="caldera-title">Caldera - Gestión Inteligente de Energía</h2>

            <div style={{marginTop: '20px', marginBottom: '20px'}}>
              <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px'}}>
                <span style={{fontSize: '14px', fontWeight: '500', color: '#2d3748'}}>{mapStatusToDescription(calderaStatus || etapaNombre)}</span>
                <span style={{fontSize: '14px', fontWeight: '600', color: calderaStatus === 'FAULT' ? '#c53030' : '#ec673b'}}>{Math.round(calderaProgress)}%</span>
              </div>
              <div style={{
                width: '100%',
                height: '12px',
                backgroundColor: '#e2e8f0',
                borderRadius: '6px',
                overflow: 'hidden',
                boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
              }}>
                <div className={((calderaStatus||'').toString().toUpperCase().includes('PURGE') || (etapaNombre||'').toString().toUpperCase().includes('PURG')) ? 'purge-pulse' : ''} style={{
                  width: `${calderaProgress}%`,
                  height: '100%',
                  backgroundColor: getBarColor(),
                  transition: 'width 0.3s ease-in-out',
                  borderRadius: '6px'
                  }} />
              </div>

              <div style={{marginTop: '8px', display:'flex', gap: '12px', alignItems: 'flex-start'}}>
                <div style={{flex:1, padding: '10px', background: '#fff', borderRadius: '6px', boxShadow: '0 1px 3px rgba(0,0,0,0.04)'}}>
                  <div style={{fontSize:'13px', color:'#4a5568', fontWeight:600}}>Etapa actual</div>
                  <div style={{marginTop:'6px', fontSize:'14px', color:'#2d3748'}}>{etapaNombre} · {Math.round(calderaProgress)}%</div>
                  <div style={{marginTop:'6px', fontSize:'12px', color:'#718096'}}>{mapStatusToDescription(calderaStatus || etapaNombre)}</div>
                  <div style={{marginTop:'8px', fontSize:'12px', color:'#4a5568'}}>
                    <div><strong>Secuencia:</strong> {calderaSequenceName} ({calderaSequenceTime}s)</div>
                    <div><strong>Voltaje flama:</strong> {calderaFlameVoltage != null ? `${calderaFlameVoltage} V` : '—'}</div>
                    <div><strong>Terminales:</strong> {Object.keys(calderaTerminals).length ? Object.entries(calderaTerminals).map(([k,v])=> `${k}:${v ? 'ON':'OFF'}`).join(', ') : '—'}</div>
                  </div>
                </div>
                <div style={{width:320, padding: '10px', background: calderaStatus === 'FAULT' ? '#fff5f5' : '#ffffff', borderRadius: '6px', boxShadow: '0 1px 3px rgba(0,0,0,0.04)'}}>
                  <div style={{fontSize:'13px', color:'#4a5568', fontWeight:600}}>Alertas recientes</div>
                  <div style={{marginTop:'8px', maxHeight: '80px', overflow:'auto'}}>
                    {alerts.slice(0,3).map((a,i)=> (
                      <div key={i} style={{padding:'6px 0', borderBottom: i<2 ? '1px solid #f1f5f9' : 'none'}}>
                        <div style={{fontSize:'13px', fontWeight:700}}>{a.title}</div>
                        <div style={{fontSize:'12px', color:'#718096'}}>{a.description}</div>
                        <div style={{fontSize:'11px', color:'#a0aec0'}}>{new Date(a.time).toLocaleString()}</div>
                      </div>
                    ))}
                    {alerts.length === 0 && <div style={{fontSize:'12px', color:'#718096'}}>No hay eventos recientes</div>}
                  </div>
                </div>
              </div>
            </div>
            
              <p className="caldera-description"></p>
              {calderaStatus === 'FAULT' && (
                <div style={{
                  marginTop: '10px',
                  padding: '10px',
                  backgroundColor: '#fed7d7',
                  color: '#c53030',
                  borderRadius: '6px',
                  fontWeight: '600'
                }}>
                  🔴 {calderaError || 'Error desconocido'}
                </div>
              )}

              {calderaStatus === 'OFFLINE' && (
                <div style={{
                  marginTop: '10px',
                  padding: '10px',
                  backgroundColor: '#edf2f7',
                  color: '#4a5568',
                  borderRadius: '6px',
                  fontWeight: '600'
                }}>
                  ⚫ Dispositivo desconectado
                </div>
              )}
            {/* Visual vertical sequence indicator */}
            <div style={{display:'flex', gap:20, alignItems:'flex-start', marginTop:20, marginBottom:20}}>
              <div style={{width:220, padding:12, background:'#fff', borderRadius:8, boxShadow:'0 1px 3px rgba(0,0,0,0.04)'}}>
                <div style={{fontSize:13, fontWeight:700, color:'#4a5568', marginBottom:8}}>Secuencia de arranque</div>
                <div style={{display:'flex', position:'relative'}}>
                  <div style={{width:24, display:'flex', justifyContent:'center'}}>
                    <div style={{width:2, background:'#e2e8f0', position:'absolute', left:11, top:20, bottom:12}} />
                  </div>
                  <div style={{flex:1}}>
                    {(() => {
                      const steps = [
                        { key: 'INICIO', label: 'Inicio' },
                        { key: 'STANDBY', label: 'Espera (Standby)' },
                        { key: 'PURGA', label: 'Purgado (Purge)' },
                        { key: 'PILOT', label: 'Piloto (Pilot)' },
                        { key: 'MAIN', label: 'Ignición principal' },
                        { key: 'RUN', label: 'Marcha (Run)' }
                      ];

                      function resolveIndexFromText(text) {
                        if (!text) return -1;
                        const s = String(text).toUpperCase();
                        if (s.includes('INICIO')) return 0;
                        if (s.includes('STANDBY') || s.includes('ESPERA')) return 1;
                        if (s.includes('HOLD')) {
                          // treat HOLD as the PURGA stage visually (do not advance)
                          if (s.includes('PURG')) return 2;
                        }
                        if (s.includes('PURGE') || s.includes('PURGA') || s.includes('PURG')) return 2;
                        if (s.includes('PILOT') || s.includes('PILOTO') || s.includes('IGNICION_PILOTO') || s.includes('IGNIT')) return 3;
                        if (s.includes('MAIN') || s.includes('PRINCIPAL') || s.includes('IGNICION_PRINCIPAL')) return 4;
                        if (s.includes('MARCHA') || s.includes('RUN')) return 5;
                        return -1;
                      }

                      const currentText = (etapaNombre || calderaSequenceName || calderaStatus || '').toString();
                      const currentIndex = resolveIndexFromText(currentText);
                      const errorIndex = calderaError ? resolveIndexFromText(etapaNombre || calderaSequenceName || calderaStatus) : -1;

                      return steps.map((st, idx) => {
                        let state = 'pending';
                        if (errorIndex === idx) state = 'error';
                        else if (idx < currentIndex) state = 'complete';
                        else if (idx === currentIndex) state = 'active';

                        // if no resolvable current index, infer by progress
                        if (currentIndex === -1) {
                          const p = Number(calderaProgress || 0);
                          let inferred = 0;
                          if (p >= 90) inferred = 5; // Run
                          else if (p >= 70) inferred = 4; // Ignición principal
                          else if (p >= 40) inferred = 3; // Piloto
                          else if (p > 10 && p < 40) inferred = 2; // Purga
                          else if (p === 10) inferred = 1; // Standby exact mapping
                          else inferred = 0; // Inicio

                          if (idx < inferred) state = 'complete';
                          else if (idx === inferred) state = 'active';
                          else state = 'pending';
                        }

                        const colors = { complete: '#38a169', active: '#ecc94b', error: '#f56565', pending: '#cbd5e1' };
                        const bg = colors[state] || colors.pending;

                        return (
                          <div key={st.key} style={{display:'flex', gap:12, alignItems:'center', padding:'6px 0'}}>
                            <div style={{width:24, display:'flex', justifyContent:'center'}}>
                              <div style={{width:18, height:18, borderRadius:9, background:bg, display:'flex', alignItems:'center', justifyContent:'center', color:'#1a202c', fontWeight:700}}>
                                {state === 'complete' ? '✓' : (idx+1)}
                              </div>
                            </div>
                            <div style={{flex:1}}>
                              <div style={{fontSize:13, fontWeight:600, color: state === 'pending' ? '#718096' : '#2d3748'}}>{st.label}</div>
                              <div style={{fontSize:11, color:'#718096'}}>{(idx === currentIndex) ? `${calderaSequenceTime}s` : ''}</div>
                            </div>
                          </div>
                        );
                      });
                    })()}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="stats-grid">
          <div className="stat-card">
              <div className="stat-header"><span className="stat-label">Dispositivos Totales</span><div className="stat-icon primary">📱</div></div>
              <div className="stat-value">{stats.total}</div>
              <div className="stat-change positive"><span>↑ 0%</span> <span style={{color:'#718096'}}>vs. mes anterior</span></div>
            </div>

            <div className="stat-card">
              <div className="stat-header"><span className="stat-label">En Línea</span><div className="stat-icon success">✅</div></div>
              <div className="stat-value">{stats.online}</div>
              <div className="stat-change positive"><span>↑ 0%</span> <span style={{color:'#718096'}}>último día</span></div>
            </div>

            <div className="stat-card">
              <div className="stat-header"><span className="stat-label">Alertas</span><div className="stat-icon warning">⚠️</div></div>
              <div className="stat-value">{stats.alerts}</div>
              <div className="stat-change negative"><span>↓ 0%</span> <span style={{color:'#718096'}}>vs. ayer</span></div>
            </div>

            <div className="stat-card">
              <div className="stat-header"><span className="stat-label">Uso Promedio</span><div className="stat-icon info">⚡</div></div>
              <div className="stat-value">{stats.usage}<span style={{fontSize:'1.2rem', color:'#718096'}}>v</span></div>
              <div className="stat-change positive"><span>↑ 0%</span> <span style={{color:'#718096'}}>esta semana</span></div>
            </div>
        </div>

        <div className="charts-grid">
          <div className="chart-card">
            <div className="chart-header"><h3 className="chart-title">Consumo de Energía</h3><span className="chart-period">Últimos 7 días</span></div>
            <div className="chart-container"><canvas ref={energyRef} className="chart-canvas" /></div>
          </div>

          <div className="chart-card">
            <div className="chart-header"><h3 className="chart-title">Dispositivos por Ubicación</h3><span className="chart-period">Actual</span></div>
            <div className="chart-container"><canvas ref={locationRef} className="chart-canvas" /></div>
          </div>

          <div className="chart-card">
            <div className="chart-header"><h3 className="chart-title">Tipos de Dispositivos</h3><span className="chart-period">Total</span></div>
            <div className="chart-container"><canvas ref={typesRef} className="chart-canvas" /></div>
          </div>

          <div className="alerts-card">
            <div className="alerts-header"><h3 className="alerts-title">Alertas Recientes</h3></div>
            {alerts.length === 0 ? (
              <div className="alert-item"><div className="alert-content"><div className="alert-title">No hay alertas recientes</div></div></div>
            ) : alerts.map((a, i) => (
              <div className="alert-item" key={i}><div className="alert-icon warning">⚠️</div><div className="alert-content"><div className="alert-title">{a.title}</div><div className="alert-description">{a.description}</div><div className="alert-time">{new Date(a.time).toLocaleString()}</div></div></div>
            ))}
          </div>
        </div>

        <div className="table-card">
          <div className="table-header"><h3 className="table-title">Dispositivos Activos</h3></div>
          <table className="device-table">
            <thead>
              <tr>
                <th>Estado</th>
                <th>Dispositivo</th>
                <th>Tipo</th>
                <th>Consumo</th>
                <th>Última Actividad</th>
                <th>Rol</th>
                <th>LED</th>
                <th>Detalle</th>
              </tr>
            </thead>
            <tbody>
              {devices.length === 0 ? (
                <tr><td colSpan="8" style={{textAlign:'center', padding:'2rem'}}>No hay dispositivos asignados o conectados aún.</td></tr>
              ) : devices.map((d) => (
                <tr key={d.token ?? d.device_id ?? d.id}>
                  <td>
                    <span className={`status-badge ${((d.status_is_online ?? d.is_online) ? 'online' : 'offline')}`}>
                      <span className={`status-dot ${((d.status_is_online ?? d.is_online) ? 'online' : 'offline')}`}/> {(d.status_is_online ?? d.is_online) ? 'En línea' : 'Fuera de línea'}
                    </span>
                  </td>
                  <td>
                    <div className="device-name">{d.name || `Dispositivo ${d.id || d.device_id}`}</div>
                    <div className="device-location">{d.location ? `📍 ${d.location}` : ''}</div>
                  </td>
                  <td>{d.type || d.device_type || '—'}</td>
                  <td>{(d.current_consumption ?? d.consumption) != null ? `${d.current_consumption ?? d.consumption} kWh` : '—'}</td>
                  <td>{(d.status_last_activity ?? d.last_activity) ? new Date(d.status_last_activity ?? d.last_activity).toLocaleString() : '—'}</td>
                  <td>{d.role || '—'}</td>
                  <td>
                    <div style={{display:'flex', gap:8, alignItems:'center'}}>
                      <div style={{fontWeight:700}}>{d.led_state ? 'ON' : 'OFF'}</div>
                      <button onClick={() => toggleLed(d.token ?? d.device_id ?? d.id, !!d.led_state, d.role)} disabled={d.role && d.role !== 'admin'} style={{padding:'6px 8px', fontSize:12}}>
                        Cambiar
                      </button>
                    </div>
                  </td>
                  <td>{(d.status_is_online ?? d.is_online) ? 'Conectado' : 'Desconectado'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
